"use client";

import { useEffect, useState } from "react";
import { useLocale } from "next-intl";

import type { GeoTemplate, GeoTemplateProps } from "./GeoBlockSlot";

import { getGeoCountryFromCookie } from "@utilities/getGeoCountry";

import { GeoRegistry } from "./GeoRegistry";

export type GeoBlockWrapperProps = {
  blockType: GeoTemplate;
  defaultProps: GeoTemplateProps | undefined;
  overrideCountries: string[];
  reusableBlockId: string;
  blockId: string;
};

export function GeoBlockWrapper({
  blockType,
  defaultProps,
  overrideCountries,
  reusableBlockId,
  blockId,
}: GeoBlockWrapperProps) {
  const locale = useLocale();
  const [overrideProps, setOverrideProps] = useState<GeoTemplateProps | null>(null);
  const [needsOverride, setNeedsOverride] = useState<boolean | null>(null);

  useEffect(() => {
    const country = getGeoCountryFromCookie();
    const isOverrideCountry = !!country && overrideCountries.includes(country);

    if (!isOverrideCountry) {
      setNeedsOverride(false);
      return;
    }

    setNeedsOverride(true);

    const params = new URLSearchParams({
      rbId: reusableBlockId,
      blockId: blockId,
      template: blockType,
      locale,
    });

    let cancelled = false;
    fetch(`/next/api/geo-country?${params}`)
      .then(res => res.json())
      .then((data: { props: GeoTemplateProps | null }) => {
        if (!cancelled) {
          setOverrideProps(data.props ?? defaultProps ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOverrideProps(defaultProps ?? null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [overrideCountries, reusableBlockId, blockId, blockType, defaultProps, locale]);

  if (needsOverride === null || needsOverride === false) {
    return null;
  }

  return <GeoRegistry blockType={blockType} resolvedProps={overrideProps} defaultProps={defaultProps} />;
}
